import { useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";

import Button from "@components/buttons/Button";
import DirectionButton from "@components/buttons/DirectionButton";
import ArticleCard from "@components/cards/ArticleCard";
import Tool from "@components/carePlan/Tool";
import styles from "@components/GoalTemplate.module.css";
import ColumnGrid from "@components/layout/ColumnGrid";
import Section from "@components/layout/Section";
import LegalDocumentTracker from "@components/LegalDocumentTracker";
import MedicationTracker from "@components/MedicationTracker";
import ProviderTracker from "@components/ProviderTracker";
import Icon from "@components/text/Icon";
import IconBase from "@components/text/IconBase";
import IconBlock from "@components/text/IconBlock";
import BodyCopy from "@components/typography/BodyCopy";
import Heading1 from "@components/typography/Heading1";
import useCreateGoal from "@hooks/mutations/useCreateGoal";
import useMyExpert from "@hooks/useMyExpert";
import useOpener from "@hooks/useOpener";
import useSanitizeHtml from "@hooks/useSanitizeHtml";
import { JourneyStatusEnum } from "@typing/Enums";
import { GetJourneyWithPlanQuery, GoalTemplateFragment, UserTypeEnum } from "@typing/Generated";
import { groupTools, medicationsBatched, providersBatched } from "@utils/modelUtils";
import { journeyRoute } from "@utils/routeUtils";

type Props = {
  goalTemplate: GoalTemplateFragment;
  journey: GetJourneyWithPlanQuery["journey"];
};

type CarePlanTaskTemplateProps = {
  journeyId: string;
  template: GoalTemplateFragment["carePlanTaskTemplates"][0];
};

const CarePlanTaskTemplate = ({ journeyId, template }: CarePlanTaskTemplateProps) => {
  const sanitizedBody = useSanitizeHtml(template?.body, journeyId, template);

  return (
    <div key={template.id}>
      <div>
        <div className={styles.flex}>
          <div>
            <IconBlock color="success-500" icon="checkmark" />
          </div>

          {sanitizedBody}
        </div>
      </div>
    </div>
  );
};

const GoalTemplate = ({ goalTemplate, journey }: Props) => {
  const myExpert = useMyExpert();
  const [loading, setLoading] = useState(false);

  const legalOpener = useOpener();
  const medicationTrackerOpener = useOpener();
  const providerOpener = useOpener();

  const createGoal = useCreateGoal({ journey });

  const handleCreateGoal = () => {
    setLoading(true);
    createGoal({
      draft: false,
      goalTemplateId: goalTemplate.id,
      journeyId: journey.id
    }).then(() => {
      setLoading(false);
    });
  };

  const goalAdded = journey.goals.some(goal => goal.goalTemplate?.id === goalTemplate.id);

  const tools = useMemo(() => {
    const groups = groupTools({
      carePlanTaskTemplates: goalTemplate.carePlanTaskTemplates,
      goals: [goalTemplate],
      journey
    });

    groups.new = groups.new.concat(
      goalTemplate.worksheetTemplates.map(template => ({ status: JourneyStatusEnum.NEW, worksheetTemplate: template }))
    );

    return groups.new.concat(groups.inProgress).concat(groups.completed);
  }, [goalTemplate, journey]);

  const batchedMedications = medicationsBatched(journey.medications);

  const batchedProviders = providersBatched(journey.providers);

  const sanitizedDescription = useSanitizeHtml(goalTemplate.description, journey.id, goalTemplate);

  return (
    <div>
      <div className={styles.navigation}>
        <DirectionButton direction="back" />

        <Icon color="primary" icon="chevronRight" />
        <BodyCopy bold color="primary">
          <FormattedMessage id="pages.carePlan.base.goals.goals" />
        </BodyCopy>
        <BodyCopy bold color="primary-300">
          {goalTemplate.title}
        </BodyCopy>
      </div>
      <div className={styles.headerContainer}>
        <ColumnGrid columns={2} focusSide="left">
          <div className={styles.header}>
            <div>
              <div className={styles.handHeartContainer}>
                <Icon color="white" icon="handHoldingHeart" />
              </div>
            </div>

            <div>
              <Heading1>{goalTemplate.title}</Heading1>
              <div>
                <BodyCopy color="gray-500">{sanitizedDescription}</BodyCopy>
              </div>
              <div className={styles.actions}>
                <Button disabled={goalAdded || loading} fill="clear" onClick={handleCreateGoal} size="large">
                  <IconBase icon={goalAdded ? "check" : "add"} slot="end" />
                  <FormattedMessage id={goalAdded ? "pages.goalTemplates.goalAdded" : "pages.goalTemplates.addGoal"} />
                </Button>
                {goalAdded && (
                  <Button
                    fill="clear"
                    routerLink={journeyRoute({ journeyId: journey.id, linkAfterJourney: "/care-plan", myExpert })}
                    size="large"
                  >
                    <IconBase icon="eye" slot="end" />
                    <FormattedMessage id="pages.goalTemplates.reviewCarePlan" />
                  </Button>
                )}
                <div />
              </div>
            </div>
          </div>
          <div />
        </ColumnGrid>
      </div>

      <ColumnGrid columns={2} focusSide="left">
        <div>
          {goalTemplate.carePlanTaskTemplates.filter(template => template.userType === UserTypeEnum.CLIENT).length >
            0 && (
            <Section
              color="success-500"
              descriptionKey="pages.carePlan.sectionTitles.actionItems.description"
              icon="checkmark"
              titleKey="pages.carePlan.sectionTitles.actionItems.title"
            >
              <div className={styles.actionItems}>
                {goalTemplate.carePlanTaskTemplates
                  .filter(template => template.userType === UserTypeEnum.CLIENT)
                  .map(template => (
                    <CarePlanTaskTemplate journeyId={journey.id} key={template.id} template={template} />
                  ))}
              </div>
            </Section>
          )}

          {(goalTemplate.tools.length > 0 || goalTemplate.worksheetTemplates.length > 0) && (
            <Section
              color="secondary"
              descriptionKey="pages.carePlan.sectionTitles.tools.description"
              icon="pencil"
              titleKey="pages.carePlan.sectionTitles.tools.title"
            >
              <div className={styles.tools}>
                {tools.map(tool => (
                  <Tool
                    archived={false}
                    journeyId={journey.id}
                    key={tool.status ?? tool.worksheet?.id}
                    legalOpener={legalOpener}
                    medicationTrackerOpener={medicationTrackerOpener}
                    providerOpener={providerOpener}
                    tool={tool}
                  />
                ))}
              </div>
            </Section>
          )}
        </div>

        {goalTemplate.articles.length > 0 && (
          <Section color="danger-500" icon="globe" titleKey="pages.carePlan.sectionTitles.learn.title">
            <div className={styles.articlesContainer}>
              {goalTemplate.articles.map(article => (
                <div className={styles.articleCardSpacing} key={article.id}>
                  <ArticleCard article={article} journeyId={journey.id} key={article.id} />
                </div>
              ))}
            </div>
          </Section>
        )}
      </ColumnGrid>
      <LegalDocumentTracker journey={journey} legalDocumentsOpener={legalOpener} />
      <ProviderTracker
        batchedProviders={batchedProviders}
        journey={journey}
        providerTrackerOpener={providerOpener}
        restartWizard
      />
      <MedicationTracker
        batchedMedications={batchedMedications}
        journey={journey}
        medicationTrackerOpener={medicationTrackerOpener}
        restartWizard
      />
    </div>
  );
};

export default GoalTemplate;
